import { pushArticleEvent } from './common/article-tracking';

const SCROLL_TOLERANCE = 8;
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

const header = document.querySelector<HTMLElement>('[data-js-header]');
const menu = document.querySelector<HTMLElement>('[data-js-menu]');
const panel = menu?.querySelector<HTMLElement>('.menu__panel') ?? null;
const openButtons = document.querySelectorAll<HTMLElement>('[data-js-header-menu-open]');
const closeButton = menu?.querySelector<HTMLElement>('[data-js-header-menu-close]') ?? null;
const overlay = menu?.querySelector<HTMLElement>('[data-js-header-overlay]') ?? null;
const toggles = menu?.querySelectorAll<HTMLElement>('[data-js-header-toggle]') ?? [];
const { body } = document;

let lastOpener: HTMLElement | null = null;
let unlockTimer: number | undefined;

const isMenuOpen = (): boolean => menu?.classList.contains('menu_opened') ?? false;

const setOpenersExpanded = (expanded: boolean): void => {
    openButtons.forEach((button) => button.setAttribute('aria-expanded', String(expanded)));
};

const lockScroll = (): void => {
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    body.classList.add('body-menu_opened');
    body.style.setProperty('--scrollbar-width', `${Math.max(scrollbarWidth, 0)}px`);
};

const unlockScroll = (): void => {
    body.classList.remove('body-menu_opened');
    body.style.removeProperty('--scrollbar-width');
};

const panelTransitionMs = (): number => {
    if (!panel) {
        return 0;
    }

    const seconds = parseFloat(getComputedStyle(panel).transitionDuration);

    return Number.isFinite(seconds) ? seconds * 1000 : 0;
};

const openMenu = (opener: HTMLElement | null): void => {
    if (!menu || isMenuOpen()) {
        return;
    }

    window.clearTimeout(unlockTimer);
    lastOpener = opener;
    header?.classList.remove('header_hidden');
    menu.classList.add('menu_opened');
    menu.setAttribute('aria-hidden', 'false');
    setOpenersExpanded(true);
    lockScroll();
    closeButton?.focus();

    // DEV-22919: which button opened the menu — header_more (desktop) or header_burger.
    const openerName = opener?.dataset.jsHeaderMenuOpener;

    if (openerName) {
        pushArticleEvent('navigation_menu', `navigation_menu_open > ${openerName}`);
    }
};

const closeMenu = (): void => {
    if (!menu || !isMenuOpen()) {
        return;
    }

    menu.classList.remove('menu_opened');
    menu.setAttribute('aria-hidden', 'true');
    setOpenersExpanded(false);
    lastOpener?.focus();
    lastOpener = null;

    const duration = panelTransitionMs();

    window.clearTimeout(unlockTimer);

    if (duration > 0) {
        unlockTimer = window.setTimeout(unlockScroll, duration);
    } else {
        unlockScroll();
    }
};

const setGroupExpanded = (toggle: HTMLElement, expanded: boolean): void => {
    toggle.setAttribute('aria-expanded', String(expanded));
    toggle.closest('.menu__item')?.classList.toggle('menu__item_expanded', expanded);
};

const trapFocus = (event: KeyboardEvent): void => {
    if (!panel || event.key !== 'Tab') {
        return;
    }

    const focusable = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (element) => element.offsetParent !== null,
    );

    if (focusable.length === 0) {
        return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement as HTMLElement | null;

    if (event.shiftKey && (active === first || !panel.contains(active))) {
        event.preventDefault();
        last.focus();
    } else if (!event.shiftKey && (active === last || !panel.contains(active))) {
        event.preventDefault();
        first.focus();
    }
};

openButtons.forEach((button) => {
    button.addEventListener('click', () => openMenu(button));
});

closeButton?.addEventListener('click', closeMenu);
overlay?.addEventListener('click', closeMenu);

toggles.forEach((toggle) => {
    toggle.addEventListener('click', () => {
        setGroupExpanded(toggle, toggle.getAttribute('aria-expanded') !== 'true');
    });
});

document.addEventListener('keydown', (event) => {
    if (!isMenuOpen()) {
        return;
    }

    if (event.key === 'Escape') {
        closeMenu();

        return;
    }

    trapFocus(event);
});

let lastScrollY = window.scrollY;
let scrollTicking = false;

const updateHeaderVisibility = (): void => {
    if (!header) {
        return;
    }

    const scrollY = Math.max(window.scrollY, 0);
    const delta = scrollY - lastScrollY;

    if (isMenuOpen()) {
        lastScrollY = scrollY;

        return;
    }

    if (Math.abs(delta) < SCROLL_TOLERANCE) {
        return;
    }

    if (delta > 0 && scrollY > header.offsetHeight) {
        header.classList.add('header_hidden');
    } else if (delta < 0) {
        header.classList.remove('header_hidden');
    }

    lastScrollY = scrollY;
};

window.addEventListener(
    'scroll',
    () => {
        if (scrollTicking) {
            return;
        }

        scrollTicking = true;
        window.requestAnimationFrame(() => {
            updateHeaderVisibility();
            scrollTicking = false;
        });
    },
    { passive: true },
);

export {};
